import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CookieConsentContextType {
  consentGiven: boolean | null;
  setConsentGiven: (consent: boolean) => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consentGiven, setConsentGivenState] = useState<boolean | null>(null);
  const [showBanner, setShowBannerState] = useState<boolean>(false);

  // Check for stored consent on initial load
  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    
    if (storedConsent !== null) {
      setConsentGivenState(storedConsent === 'true');
      setShowBannerState(false);
    } else {
      // If no choice has been made, show the banner
      setShowBannerState(true);
    }
  }, []);

  // Function to set consent and update localStorage
  const setConsentGiven = (consent: boolean) => {
    localStorage.setItem('cookieConsent', consent.toString());
    setConsentGivenState(consent);
    setShowBannerState(false);
  };

  // Function to control banner visibility
  const setShowBanner = (show: boolean) => {
    setShowBannerState(show);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consentGiven,
        setConsentGiven,
        showBanner,
        setShowBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
